import React, { FC, useEffect, useState } from 'react';
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from '@mui/material';
import styled from 'styled-components';
import NoResults from '@Components/NoResults/NoResults';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { videoProvidersContainersFetch } from '../Slices/VideoProviderSettings.slice';

const KIND_LABELS: Record<string, string> = {
	library: 'Library',
	collection: 'Collection',
	folder: 'Folder',
};

const SCOPE_LABELS: Record<string, string> = {
	instructor: 'Instructor',
	classroom: 'Classroom',
	course: 'Course',
	standalone_course: 'Standalone course',
};

/**
 * Read-only registry of provisioned provider containers (which instructor got
 * which Bunny library / VdoCipher folder chain). Local pagination on purpose —
 * two URL-driven MainTables on one page would fight over ?page.
 */
const ContainersTable: FC = () => {
	const dispatch = useAppThunkDispatch();
	const { containers } = useTypedSelector(
		(state) => state.VideoProviderSettings
	);
	const [page, setPage] = useState(0);
	const [perPage, setPerPage] = useState(10);

	useEffect(() => {
		dispatch(
			videoProvidersContainersFetch({ page: page + 1, per_page: perPage })
		);
	}, [dispatch, page, perPage]);

	return (
		<Section>
			<Typography variant="h6">Provisioned containers</Typography>
			<Caption variant="caption">
				Created lazily by imports; the provider dashboards mirror this
				registry. Renames do not propagate.
			</Caption>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<HeadRow>
							<TableCell>ID</TableCell>
							<TableCell>Provider</TableCell>
							<TableCell>Kind</TableCell>
							<TableCell>Scope</TableCell>
							<TableCell>Scope ID</TableCell>
							<TableCell>External ID</TableCell>
							<TableCell>Parent</TableCell>
							<TableCell>Created</TableCell>
						</HeadRow>
					</TableHead>
					<TableBody>
						{containers.list.length === 0 && (
							<TableRow>
								<TableCell colSpan={8}>
									<NoResults />
								</TableCell>
							</TableRow>
						)}
						{containers.list.map((container) => (
							<TableRow key={container.id} hover>
								<TableCell>{container.id}</TableCell>
								<TableCell>
									{container.provider === 'bunny' ? 'Bunny Stream' : 'VdoCipher'}
								</TableCell>
								<TableCell>
									{KIND_LABELS[container.kind] ?? container.kind}
								</TableCell>
								<TableCell>
									{SCOPE_LABELS[container.scopeType] ?? container.scopeType}
								</TableCell>
								<TableCell>{container.scopeId}</TableCell>
								<TableCell>
									<ExternalId>{container.externalId}</ExternalId>
								</TableCell>
								<TableCell>{container.parentId ?? '-'}</TableCell>
								<TableCell>
									<DateTimeFormatter date={new Date(container.createdAt)} />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={containers.meta?.total ?? 0}
				page={page}
				onPageChange={(event, newPage) => setPage(newPage)}
				rowsPerPage={perPage}
				onRowsPerPageChange={(event) => {
					setPerPage(Number(event.target.value));
					setPage(0);
				}}
				rowsPerPageOptions={[10, 25, 50]}
			/>
		</Section>
	);
};

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const Caption = styled(Typography)`
	color: rgba(0, 0, 0, 0.6);
`;

const HeadRow = styled(TableRow)`
	th {
		text-transform: uppercase;
		font-weight: 600;
		white-space: nowrap;
	}
`;

const ExternalId = styled.span`
	font-family: monospace;
	font-size: 0.8rem;
	word-break: break-all;
`;

export default ContainersTable;
